import React from "react";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {type ICalendarPickerController} from "views/components/calendar_picker/calendar_picker.controller";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {DateCalendar} from "@mui/x-date-pickers/DateCalendar";
import {Typography, Drawer, Button} from "@mui/material";
import {PickersDay} from "@mui/x-date-pickers/PickersDay";
import styled from "@emotion/styled";
import {ContestStatus} from "data/enums";
import {Exist} from "views/components/exist/exist.component";
import {AdapterLuxon} from "@mui/x-date-pickers/AdapterLuxon";
import {DateTime} from "luxon";

const Picker = styled.div`
	border-radius: 8px 8px 0px 0px;
	overflow: hidden;
	background: #000;
	color: #fff;
	padding-bottom: 32px;
	.MuiDateCalendar-root {
		height: auto;
	}
	.MuiPickersCalendarHeader-root {
		width: 100%;
		padding: 0;
		position: relative;
		margin: 0;
		height: 40px;
		max-height: 40px;
	}

	.MuiPickersCalendarHeader-labelContainer {
		margin: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		width: 100%;
		color: #fff;

		text-align: center;
		font-family: var(--fontFamilyBase);
		font-size: 16px;
		font-style: normal;
		font-weight: 600;
		line-height: normal;
	}

	.MuiPickersArrowSwitcher-root {
		position: absolute;
		width: 100%;
		left: 0;
		top: 0;
		display: flex;
		justify-content: space-between;

		button {
			color: currentColor;

			&.Mui-disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}
	}

	.MuiDayCalendar-header {
		span {
			color: #aeaeb2;
			font-weight: 800;
		}
	}

	.MuiPickersSlideTransition-root.MuiDayCalendar-slideTransition {
		min-height: 260px;
	}

	.MuiPickersDay-root {
		border-radius: 4px;
		height: 16px;
		margin: 12px 2px;
		position: relative;

		&.Mui-disabled:not(.Mui-selected) {
			color: #8e8e93;
			cursor: not-allowed;
			pointer-events: auto;
		}

		&.Mui-selected {
		}

		&:after {
			content: "";
			display: block;
			position: absolute;
			bottom: -6px;
			left: 50%;
			transform: translateX(-50%);
			width: 8px;
			height: 4px;
			border-radius: 2px;
			background: var(--day-calendar-color);
		}
		&.${ContestStatus.Live} {
			--day-calendar-color: #99fe88;
		}

		&.${ContestStatus.Complete} {
			--day-calendar-color: #21cfdc;
		}

		&.${ContestStatus.Scheduled} {
			--day-calendar-color: #ed88ff;
		}
	}
`;

const Title = styled.div`
	padding: 16px;
	display: inline-flex;
	margin: 0 auto;
	text-align: center;
	width: 100%;
	justify-content: center;
`;

const Controls = styled.div`
	display: flex;
	justify-content: center;
	padding: 16px 0;
	button {
		min-width: 165px;
	}
`;

const Legends = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	width: 100%;
`;

const Legend = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 6px;
	div {
		width: 8px;
		height: 4px;
		border-radius: 2px;
		background: var(--day-calendar-color);

		&.${ContestStatus.Live} {
			--day-calendar-color: #99fe88;
		}

		&.${ContestStatus.Complete} {
			--day-calendar-color: #21cfdc;
		}

		&.${ContestStatus.Scheduled} {
			--day-calendar-color: #ed88ff;
		}
	}
`;

export const CalendarPicker: React.FC = observer(() => {
	const {
		i18n,
		getDayStatus,
		firstContestDate,
		lastContestDate,
		selectedDate,
		setSelectedDate,
		isOpen,
		isCurrentContestDay,
		toCurrentContestDay,
		handleClose,
	} = useViewController<ICalendarPickerController>(Bindings.CalendarPickerController);

	return (
		<Drawer
			anchor={"bottom"}
			open={isOpen}
			PaperProps={{
				sx: {
					background: "transparent",
				},
			}}
			onClose={handleClose}
			aria-labelledby="responsive-dialog-title">
			<Picker>
				<Title>
					<Typography variant={"body2"}>
						{i18n.t("calendar.title", "MLB Season 2024")}
					</Typography>
				</Title>
				<LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={"en"}>
					<DateCalendar<DateTime>
						sx={{
							color: "#fff",
						}}
						focusedView={"year"}
						slotProps={{calendarHeader: {format: "MMMM"}}}
						minDate={firstContestDate}
						maxDate={lastContestDate}
						views={["day"]}
						value={selectedDate}
						showDaysOutsideCurrentMonth={true}
						onChange={(value: DateTime) => {
							setSelectedDate(value);
						}}
						slots={{
							day: (props) => {
								const status = getDayStatus(props.day);

								return (
									<PickersDay<DateTime>
										{...props}
										className={status}
										sx={{color: "#fff"}}
										disabled={[
											ContestStatus.Open,
											ContestStatus.Scheduled,
										].includes(status)}
									/>
								);
							},
						}}
					/>
				</LocalizationProvider>

				<Exist when={!isCurrentContestDay}>
					<Controls>
						<Button variant="outlined" color="secondary" onClick={toCurrentContestDay}>
							{i18n.t("calendar.current_button", "Today")}
						</Button>
					</Controls>
				</Exist>

				<Legends>
					<Legend>
						<div className={ContestStatus.Complete} />
						<Typography variant={"miscRegular"}>
							<Typography variant={"miscRegular"}>
								{i18n.t("calendar.legend_complete", "Complete")}
							</Typography>
						</Typography>
					</Legend>
					<Legend>
						<div className={ContestStatus.Live} />
						<Typography variant={"miscRegular"}>
							{i18n.t("calendar.legend_live", "Live")}
						</Typography>
					</Legend>

					<Legend>
						<div className={ContestStatus.Scheduled} />
						<Typography variant={"miscRegular"}>
							{i18n.t("calendar.legend_tomorrow", "Tomorrow")}
						</Typography>
					</Legend>
				</Legends>
			</Picker>
		</Drawer>
	);
});
